import { useCallback, useEffect, useState } from 'react';
import { Box, Button, FormLabel } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { ArrowForward, UploadRounded } from '@mui/icons-material';
import { mask } from 'remask';

import { Input } from '../../../components/Input';
import { SelectComponent } from '../../../components/Select';
import { LogoCompanies } from '../../../components/LogoCompanies';
import { Mfa } from '../components/mfa';

import TenLogo2 from '../../../assets/horizontal-blue-logo.svg';

import { SectionInputs, PreviewArea } from './styles';

import { getNichos } from '../../../utils/companyHelpers';
import { cnpjPattern } from '../../../utils/cpfValidation';
import { acceptImageTypes } from '../../../utils/mimeTypes';

import { Company } from '../../../services';
import {
  createCompanyRequest,
  updateCompanyRequest,
} from '../../../services/requests/empresas/utils';
import { createAvatarAndLogoRequest } from '../../../services/requests/arquivos/utils';

import useAuthStore from '../../../store/auth';
import useCompanyStore from '../../../store/companies';

interface FormCriarEmpresaProps {
  handleNext?: any;
  handleReset: any;
  accountId: string;
  companyId?: string;
  setCompanyId?: any;
  setCompany?: any;
  company?: Company;
  setIsLoading: (status: boolean) => void;
  setIsLoadingText: (text: string) => void;
}

export const FormCriarEmpresa = ({
  handleNext,
  handleReset,
  accountId,
  companyId,
  setCompanyId,
  setCompany,
  company,
  setIsLoading,
  setIsLoadingText,
}: FormCriarEmpresaProps) => {
  const user = useAuthStore(state => state.user);
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const setSelectedCompany = useCompanyStore(state => state.setSelectedCompany);

  const [file, setFile] = useState<any>('');
  const [url, setUrl] = useState<any>('');
  const [nicho, setNicho] = useState<any>('');
  const [logoName, setLogoName] = useState('');
  const [logoMimetype, setLogoMimetype] = useState('');
  const [logoSize, setLogoSize] = useState(0);
  const [logoFileInBase64, setLogoFileInBase64] = useState<any>();
  const [mfa, setMfa] = useState(false);

  const formValidation = useCallback((): any => {
    return Yup.object().shape({
      nome_empresa: Yup.string()
        .min(4, 'Mínimo de 4 Dígitos')
        .required('É necessário adicionar um Nome.'),
      nome_fantasia: Yup.string().min(3, 'Mínimo de 3 Dígitos').nullable(),
      cnpj_empresa: Yup.string()
        .min(18, 'Mínimo de 4 Dígitos')
        .required('É necessário adicionar um CNPJ.'),
      nicho_empresa: Yup.string().required('É necessário adicionar um Nicho.'),
    });
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(formValidation()),
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (company) {
      setValue('nome_empresa', company?.name);
      setValue('nome_fantasia', company?.fancy_name);
      setValue('cnpj_empresa', company?.doc);
      setValue('nicho_empresa', company?.niche);
      setNicho(company?.niche);
      setUrl(company?.logo_url);
      setMfa(company?.mfa_required);
    }
  }, [company, setValue]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setMfa(event.target.checked);
    },
    [],
  );

  const handleCreateCompany = useCallback(
    async (formData: any) => {
      setIsLoadingText('Carregando Informações da Empresa');
      setIsLoading(true);
      try {
        const arquiveData = {
          original_name: logoName,
          mimetype: logoMimetype,
          size: logoSize,
          localFile: logoFileInBase64,
        };

        if (companyId) {
          let uploadedData = {
            name: formData.nome_empresa,
            fancy_name: formData.nome_fantasia,
            doc: formData.cnpj_empresa,
            niche: formData.nicho_empresa,
            logo_url: url,
            mfa_required: mfa,
          };

          if (url !== company?.logo_url && url !== '') {
            const imageResponse = await createAvatarAndLogoRequest(
              arquiveData as any,
              user?.id as string,
              selectedCompany?.id as string,
            );

            if (imageResponse?.status === 201) {
              uploadedData = {
                ...uploadedData,
                logo_url: imageResponse.data.url,
              };
            }
          }

          const response = await updateCompanyRequest(uploadedData, companyId!);

          if (response?.status === 200) {
            setCompany(response?.data);
            setSelectedCompany(response?.data);
            handleReset();
          }
        } else {
          let data = {
            name: formData.nome_empresa,
            fancy_name: formData.nome_fantasia,
            doc: formData.cnpj_empresa,
            niche: formData.nicho_empresa,
            logo_url: '',
            status: true,
            account_id: accountId,
            users: [],
            users_ids: [],
            boards: [],
            boards_ids: [],
          };

          const response = await createCompanyRequest(data);

          if (response?.status === 201) {
            const empresa = response?.data;

            if (url !== '') {
              const imageResponse = await createAvatarAndLogoRequest(
                arquiveData as any,
                user?.id as string,
                empresa?.id as string,
              );

              if (imageResponse?.status === 201) {
                const empresaData = {
                  logo_url: imageResponse.data.url,
                };

                await updateCompanyRequest(empresaData, empresa.id);
              }
            }

            setCompanyId(empresa?.id);
            setCompany(empresa);
            setSelectedCompany(empresa);
            handleNext();
            handleReset();
          }
        }
      } catch (error) {
        console.log('Error handleCreateCompany', error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      logoName,
      logoMimetype,
      logoSize,
      logoFileInBase64,
      companyId,
      url,
      mfa,
      company?.logo_url,
      user?.id,
      selectedCompany?.id,
      setCompany,
      setSelectedCompany,
      handleReset,
      accountId,
      setCompanyId,
      handleNext,
      setIsLoading,
      setIsLoadingText,
    ],
  );

  const handleLogo = useCallback((t: any) => {
    const { files } = t.target;

    let reader = new FileReader();
    reader.readAsDataURL(files[0]);

    reader.onload = () => {
      setLogoFileInBase64(reader.result);
    };
    reader.onerror = error => {
      console.log('convertFileInBase64 error => ', error);
      return null;
    };

    const fileName = files[0].name;
    const fileSize = files[0].size;
    const fileType = files[0].type;

    const newUrl = URL.createObjectURL(files[0]);

    setLogoName(fileName);
    setLogoMimetype(fileType);
    setLogoSize(fileSize);

    setFile(files[0]);
    setUrl(newUrl);
  }, []);

  return (
    <form
      onSubmit={handleSubmit(handleCreateCompany)}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      <SectionInputs>
        {url && url?.length > 0 ? (
          <>
            <PreviewArea>
              <LogoCompanies
                boxWidth="100%"
                url={url || TenLogo2}
                height="100%"
                maxWidth="100%"
                width="250px"
                justifyContent="center"
              />

              {/* <img src={url} alt="logo" /> */}
            </PreviewArea>
            <FormLabel
              htmlFor="input-file"
              className="add-logo"
              sx={{ cursor: 'pointer' }}
            >
              <input
                type="file"
                name="file"
                onChange={handleLogo}
                id="input-file"
                style={{ display: 'none' }}
              />
              <UploadRounded /> Editar logotipo
            </FormLabel>
          </>
        ) : (
          <FormLabel
            htmlFor="input-file"
            className="add-logo"
            sx={{ cursor: 'pointer' }}
          >
            <input
              type="file"
              name="file"
              value={file}
              onChange={handleLogo}
              id="input-file"
              style={{ display: 'none' }}
              accept={acceptImageTypes()}
            />
            <UploadRounded /> Inserir Logotipo
          </FormLabel>
        )}
        <Input
          name="nome_empresa"
          control={control}
          fullWidth
          label="Razão social"
        />
        <Input
          name="nome_fantasia"
          control={control}
          fullWidth
          label="Nome fantasia ou apelido"
        />
        <Input
          name="cnpj_empresa"
          control={control}
          fullWidth
          label="CNPJ"
          onChange={t =>
            setValue('cnpj_empresa', mask(t.target.value, cnpjPattern))
          }
        />
        <SelectComponent
          fullWidth
          label="Nicho"
          value={nicho}
          name="nicho_empresa"
          control={control}
          placeholder="Nicho"
          options={getNichos}
          onChange={t => {
            setValue('nicho_empresa', t.target.value);
            setNicho(t.target.value);
          }}
        />
        {company && <Mfa onChange={handleChange} value={mfa} />}
      </SectionInputs>

      <Box display="flex" width="100%" justifyContent="end" flex="0 0 0">
        <Button
          endIcon={company ? null : <ArrowForward />}
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          color="success"
          sx={{
            width: '156px',
            height: '42px',
            color: '#fff',
            minWidth: '156px',
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: '500',
            fontSize: '15px',
            lineHeight: '14px',
          }}
        >
          {company ? 'Salvar' : 'Continuar'}
        </Button>
      </Box>
    </form>
  );
};
